import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import useTeacherSearch from './useTeacherSearch';

export type OptionKind = 'assignment' | 'classroom' | 'staffer' | 'student' | 'subscriber';

export interface FilterOption {
  detail?: string;
  label: string;
  value: string;
}

export interface OptionScope {
  staffer_id?: string;
  subscriber_id?: string;
}

interface OptionRow { detail: string | null; id: number; label: string }

/** Options for one troubleshoot filter. Teachers come from the user search; the rest from grade sync. */
const useFilterOptions = (kind: OptionKind, query: string, scope: OptionScope, enabled: boolean) => {
  const q = query.trim();
  const teachers = useTeacherSearch(kind === 'staffer' && enabled ? q : '');
  const others = useQuery({
    enabled: enabled && kind !== 'staffer',
    keepPreviousData: true,
    queryFn: async (): Promise<FilterOption[]> => {
      const { data } = await axios.get(Routes.plato_api_sysadmin_grade_sync_filter_options_path(), {
        params: { kind, q, staffer_id: scope.staffer_id, subscriber_id: scope.subscriber_id },
      });

      return (data.data as OptionRow[]).map(row => ({
        detail: row.detail ?? undefined, label: row.label, value: String(row.id),
      }));
    },
    queryKey: ['gradeSyncFilterOptions', kind, q, scope.staffer_id, scope.subscriber_id],
  });

  if (kind === 'staffer') {
    return {
      isLoading: teachers.isFetching,
      options: (teachers.data ?? []).map(hit => ({
        detail: [hit.email, hit.subscriber?.name, `staffer ${hit.id}`].filter(Boolean).join(' · '),
        label: `${hit.first_name ?? ''} ${hit.last_name ?? ''}`.trim() || `Staffer ${hit.id}`,
        value: String(hit.id),
      })),
    };
  }

  return { isLoading: others.isFetching, options: others.data ?? [] };
};

export default useFilterOptions;
